import { keepPreviousData, useQuery } from "@tanstack/react-query";

import type { BenchmarkGranularity } from "app/common/benchmark/constants";
import type { BenchmarkMetricName } from "app/common/benchmark/types";
import { useToasts } from "app/common/hooks";
import { translate } from "app/filters/i18nFilter";
import type { BenchmarkTabName } from "app/pages/benchmark-tracker";
import { createBenchmarkTrackerApi } from "app/pages/benchmark-tracker";
import DurationService from "app/services/DurationService";

const SINGLE_METRIC_QUERY_KEY = "BENCHMARK_METRIC_DATA";

export const useSingleBenchmarkMetric = <R = unknown>(
    metric: BenchmarkMetricName,
    benchmarkId: string,
    country: number,
    duration: string,
    granularity: BenchmarkGranularity,
    tabName: BenchmarkTabName,
    competitors: string[] = [],
) => {
    const { showErrorToast } = useToasts();

    return useQuery<R>({
        queryKey: [
            SINGLE_METRIC_QUERY_KEY,
            { metric, benchmarkId, country, duration, granularity, tabName, competitors },
        ],
        queryFn: async () => {
            try {
                const { from } = DurationService.getDurationData(duration).forAPI;
                const startDate = from.replaceAll("|", "-");

                const api = createBenchmarkTrackerApi();
                return api.metric.fetchMetric<R>(metric, tabName, benchmarkId, country, startDate, granularity);
            } catch (error) {
                showErrorToast(translate("common.api.error", { error: error?.message }));
            }
        },
        placeholderData: keepPreviousData,
        gcTime: Infinity,
        refetchOnMount: true,
        retry: 1,
        enabled: !!benchmarkId,
    });
};
